import { Country } from "../Languages";
import { pixelByLocale } from "./useMetaPixel";

export const useAdConversionTracking = () => {
    const metaTracking = {
        leadSubmittedConversion: () => {
            // @ts-ignore
            if (!!window.fbq) {
                // @ts-ignore
                window.fbq("track", "Lead");
            }
        },
        bookedConversion: () => {
            // @ts-ignore
            if (!!window.fbq) {
                // @ts-ignore
                window.fbq("track", "Schedule");
            }
        },
        enhancedConversion: ({
            email,
            firstName,
            lastName,
            phone,
            country,
        }: {
            email: string;
            firstName?: string;
            lastName?: string;
            phone?: string;
            country: Country;
        }) => {
            // @ts-ignore
            if (!fbq) return;
            // @ts-ignore
            fbq("init", pixelByLocale[country], {
                em: normalizeAndHash(email),
                ...(phone && { ph: normalizeAndHash(phone) }),
                ...(firstName && { fn: firstName }),
                ...(lastName && { ln: lastName }),
            });
        },
    };

    const googleTracking = {
        leadSubmittedConversion: () => {
            // @ts-ignore
            window.dataLayer = window.dataLayer || [];
            function gtag() {
                // @ts-ignore
                dataLayer.push(arguments);
            }

            // @ts-ignore
            gtag("event", "conversion", {
                send_to: "AW-11396633091/gJgkCJnuq_cYEIOkq7oq",
                event_callback: () => {},
            });
        },
        bookedConversion: () => {
            // @ts-ignore
            window.dataLayer = window.dataLayer || [];
            function gtag() {
                // @ts-ignore
                dataLayer.push(arguments);
            }

            // @ts-ignore
            gtag("event", "conversion", {
                send_to: "AW-11396633091/cYXjCJ20jKcaEIOkq7oq",
                event_callback: () => {},
            });
        },
        enhancedConversion: ({
            email,
            firstName,
            lastName,
            phone,
        }: {
            email: string;
            firstName?: string;
            lastName?: string;
            phone?: string;
        }) => {
            // @ts-ignore
            window.dataLayer = window.dataLayer || [];
            function gtag() {
                // @ts-ignore
                dataLayer.push(arguments);
            }
            // @ts-ignore
            gtag("set", "user_data", {
                sha256_email_address: normalizeAndHash(email),
                ...(phone && { sha256_phone_number: normalizeAndHash(phone) }),
                ...(firstName && { "address.sha256_first_name": normalizeAndHash(firstName) }),
                ...(lastName && { "address.last_name": normalizeAndHash(lastName) }),
            });
        },
    };

    const xingTracking = {
        leadSubmittedConversion: () => {
            // @ts-ignore
            if (window.xtp) {
                // @ts-ignore
                window.xtp.xingTrk("leads");
            }
        },
    };

    const msTracking = {
        leadSubmittedConversion: async () => {
            // See here: https://ui.ads.microsoft.com/campaign/vnext/conversiongoals?aid=176409615&cid=253125303&opportunityId=5543931144584003000&opportunityType=SetupConversionTrackingOpportunity&source=webui&uid=134353376
            // prettier-ignore
            // @ts-ignore
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            await (function(w,d,t,r,u){var f,n,i;w[u]=w[u]||[],f=function(){var o={ti:"343096686", enableAutoSpaTracking: true};o.q=w[u],w[u]=new UET(o),w[u].push("pageLoad")},n=d.createElement(t),n.src=r,n.async=1,n.onload=n.onreadystatechange=function(){var s=this.readyState;s&&s!=="loaded"&&s!=="complete"||(f(),n.onload=n.onreadystatechange=null)},i=d.getElementsByTagName(t)[0],i.parentNode.insertBefore(n,i)})(window,document,"script","//bat.bing.com/bat.js","uetq");

            // @ts-ignore
            window.uetq = window.uetq || [];
            // @ts-ignore
            window.uetq.push("event", "submit_lead_form", {});
        },
    };

    const linkedInTracking = {
        leadSubmittedConversion: () => {
            // @ts-ignore
            if (!!window.lintrk) {
                // @ts-ignore
                window.lintrk("track", { conversion_id: 21462561 });
            }
        },
        meetingBookedConversion: () => {
            // @ts-ignore
            if (!!window.lintrk) {
                // @ts-ignore
                window.lintrk("track", { conversion_id: 16598628 });
            }
        },
    };

    return {
        trackLeadConversion: () => {
            metaTracking.leadSubmittedConversion();
            googleTracking.leadSubmittedConversion();
            linkedInTracking.leadSubmittedConversion();
            xingTracking.leadSubmittedConversion();
            msTracking.leadSubmittedConversion();
        },
        trackMeetingBookedConversion: () => {
            metaTracking.bookedConversion();
            googleTracking.bookedConversion();
            linkedInTracking.meetingBookedConversion();
        },
        trackGoogleConversionOnly: googleTracking.leadSubmittedConversion,
        trackMetaConversionOnly: metaTracking.leadSubmittedConversion,
        trackXingConversionOnly: xingTracking.leadSubmittedConversion,
        trackMsConversionOnly: msTracking.leadSubmittedConversion,
        trackLinkedInConversionOnly: linkedInTracking.leadSubmittedConversion,

        trackEnhancedConversion: (params: {
            email: string;
            firstName?: string;
            lastName?: string;
            phone?: string;
            country: Country;
        }) => {
            metaTracking.enhancedConversion(params);
            googleTracking.enhancedConversion(params);
        },
    };
};

const normalizeAndHash = async (param: string) => {
    // see https://support.google.com/google-ads/answer/13258081?#Add_a_code_snippet&zippy=identify-and-define-your-enhanced-conversions-fields
    const normalized = param.trim().toLowerCase();
    const hashed = await digestMessage(normalized);
    return hashed;
};

async function digestMessage(message: string) {
    // https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest
    const msgUint8 = new TextEncoder().encode(message); // encode as (utf-8) Uint8Array
    const hashBuffer = await window.crypto.subtle.digest("SHA-256", msgUint8); // hash the message
    const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join(""); // convert bytes to hex string
    return hashHex;
}
